import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiDownload, FiPrinter, FiSend } = FiIcons;

const PaymentReceiptModal = ({ isOpen, onClose, payment }) => {
  if (!payment) return null;

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPaymentMethodName = (method) => {
    const methods = {
      bank_transfer: 'Bank Transfer',
      credit_card: 'Credit Card',
      paypal: 'PayPal',
      stripe: 'Stripe',
      wire_transfer: 'Wire Transfer',
      check: 'Check',
      cash: 'Cash',
      other: 'Other'
    };
    return methods[method] || method;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    console.log('Download receipt for payment:', payment.receipt_number);
  };

  const handleSend = () => {
    console.log('Send receipt for payment:', payment.receipt_number);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-2xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10 print:hidden">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Payment Receipt</h2>
                  <p className="text-sm text-gray-600">{payment.receipt_number}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handlePrint}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Print Receipt"
                  >
                    <SafeIcon icon={FiPrinter} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleDownload}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    title="Download PDF"
                  >
                    <SafeIcon icon={FiDownload} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleSend}
                    className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors duration-200"
                    title="Send Receipt"
                  >
                    <SafeIcon icon={FiSend} className="w-5 h-5" />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Receipt Content */}
            <div className="p-8 print:p-4">
              {/* Receipt Header */}
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">PAYMENT RECEIPT</h1>
                <p className="text-lg text-green-600 font-semibold">Payment Confirmed</p>
              </div>

              {/* Receipt Details */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Receipt Information</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Receipt Number:</span>
                        <p className="font-semibold">{payment.receipt_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Payment Date:</span>
                        <p className="font-semibold">{formatDate(payment.payment_date)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Transaction ID:</span>
                        <p className="font-semibold">{payment.transaction_id || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-3">Client Information</h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Business Name:</span>
                        <p className="font-semibold">{payment.client?.business_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Contact Person:</span>
                        <p className="font-semibold">{payment.client?.name}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Email:</span>
                        <p className="font-semibold">{payment.client?.email}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-green-50 rounded-lg p-6 mb-6 border border-green-200">
                <h3 className="font-semibold text-gray-800 mb-4">Payment Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Invoice Number:</span>
                    <span className="font-semibold">{payment.invoice_number}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-semibold">{getPaymentMethodName(payment.payment_method)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Payment Amount:</span>
                    <span className="font-bold text-lg text-green-600">{formatCurrency(payment.paid_amount)}</span>
                  </div>
                  {payment.late_fee > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Late Fee:</span>
                      <span className="font-semibold text-red-600">{formatCurrency(payment.late_fee)}</span>
                    </div>
                  )}
                  {payment.remaining_balance > 0 && (
                    <div className="flex justify-between items-center border-t pt-3">
                      <span className="text-gray-600">Remaining Balance:</span>
                      <span className="font-semibold text-orange-600">{formatCurrency(payment.remaining_balance)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Notes */}
              {payment.payment_notes && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h4 className="font-semibold text-gray-800 mb-2">Payment Notes:</h4>
                  <p className="text-gray-600 text-sm">{payment.payment_notes}</p>
                </div>
              )}

              {/* Company Information */}
              <div className="border-t border-gray-200 pt-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">From:</h4>
                    <div className="text-sm text-gray-600">
                      <p className="font-semibold text-gray-800">Agency Name</p>
                      <p>123 Business Street</p>
                      <p>City, State 12345</p>
                      <p>Phone: (555) 123-4567</p>
                      <p>Email: billing@agency.com</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">Payment Status:</h4>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiIcons.FiCheckCircle} className="w-5 h-5 text-green-500" />
                      <span className="text-green-600 font-semibold">Payment Received</span>
                    </div>
                    {payment.remaining_balance === 0 ? (
                      <p className="text-sm text-green-600 mt-1">Invoice fully paid</p>
                    ) : (
                      <p className="text-sm text-orange-600 mt-1">Partial payment - balance remaining</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-sm text-gray-500 mt-8 pt-6 border-t">
                <p>Thank you for your payment!</p>
                <p>This is an automatically generated receipt.</p>
                <p className="mt-2">Generated on {formatDate(new Date().toISOString())}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PaymentReceiptModal;